import type { PopoverInterface, PopoverOptions } from "flowbite";
import { Popover } from "flowbite";
import moment from "moment-timezone";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable.js";
import { EventBus } from "@/lib/event_bus";
import { generateUID } from "@/lib/helpers";
import { trackMobileWebEvent } from "@/lib/pendo";
import provideHook from "@/lib/provideHook";
import { withStartParam } from "@/lib/scheduleStartParam";
import { getQueryVariables, updateQueryParam } from "@/lib/url";

import AdminTimeClockChannel from "@/channels/AdminTimeClockChannel.js";
import { breakpoints } from "@/mixins";
import { jobShowPath, userShowPath } from "@/mixins/path_helpers";
import EventCalendar from "@/mobiscroll/EventCalendar";
import { useScheduleColors } from "@/SchedulerV2/useScheduleColors";
import { AdminPreference } from "@/typings/adminPreferences";
import { TimelineEvent } from "@/typings/scheduler";
import { EventAPIResponse, ResourceAPIResponse } from "@/typings/scheduler";
import {
  MbscCellClickEvent,
  MbscEventcalendarOptions,
  MbscEventClickEvent
} from "@mobiscroll/javascript";
import { getUnavailableColorsFromBusinessHours } from "./getUnavailableColorsForResource";

// Interface for using EventCalendar
export const useEventCalendar = provideHook(() => {
  const store = useStore();
  const { getStatusCustomizationName } = useScheduleColors();
  const calendar = ref<EventCalendar | null>(null);
  const activeResource = ref<ResourceAPIResponse | null>(null);
  const eventPoll = ref<ReturnType<typeof setInterval> | null>(null);
  const selectedAppointmentId = ref<string | number | undefined | null>(null);
  const channel = ref<AdminTimeClockChannel | null>(null);
  const selectedEventId = ref<string | number | undefined | null>(null);

  const isVisible = (event: EventAPIResponse) => {
    const { jobStatusIds, departmentIds, serviceCategoryIds, statuses } =
      filters.value;

    if (jobStatusIds.length) {
      if (!jobStatusIds.includes(event.appointment?.job_status?.key || ""))
        return false;
    }

    if (departmentIds.length && event.technician) {
      if (!departmentIds.includes(event.technician?.department.id))
        return false;
    }

    if (serviceCategoryIds.length) {
      if (!serviceCategoryIds.includes(event.appointment?.service_category_id))
        return false;
    }

    if (statuses.length) {
      const status = getStatusCustomizationName(event.appointment);
      if (!statuses.includes(status)) return false;
    }

    return true;
  };
  const filters = computed(() => store.state.timeline.filters);
  const events = computed<EventAPIResponse[]>(() => {
    const allEvents: EventAPIResponse[] = [
      ...store.state.timeline.events
    ].reduce((_events: EventAPIResponse[], event: EventAPIResponse) => {
      if (event.appointment?.ghost) {
        _events.push({
          ...event,
          appointment: {
            ...event.appointment,
            ghost: false,
            unassigned: false
          },
          technician: event.ghost_technician,
          is_ghost: true,
          id: generateUID()
        });
      }
      _events.push(event);
      return _events;
    }, []);
    return allEvents.filter((event) => isVisible(event));
  });
  const unassignedRowEnabled = computed(() => {
    const preference: AdminPreference | undefined =
      store.state.adminPreferences.find(
        (preference: AdminPreference) =>
          preference.key === "sheduler_unassigned_events_floating"
      );
    return !preference?.enabled;
  });
  const resources = computed<
    | ResourceAPIResponse[]
    | { id: number; full_name: string; avatar: any; department: any }[]
  >(() => {
    const resources:
      | ResourceAPIResponse
      | { id: number; full_name: string; avatar: any; department: any }[] = [];
    if (unassignedRowEnabled.value) {
      resources.push({
        id: -1,
        full_name: "Unassigned",
        avatar: {},
        department: {}
      });
    }
    const filteredTechs = filters.value?.departmentIds.length
      ? store.state.timeline.resources.filter((tech: ResourceAPIResponse) => {
          return filters.value.departmentIds.some((deptId) =>
            tech.belongs_to_departments.includes(deptId)
          );
        })
      : store.state.timeline.resources;
    return resources.concat(
      filteredTechs
        .slice()
        .sort((a: ResourceAPIResponse, b: ResourceAPIResponse) =>
          a.full_name < b.full_name ? -1 : 1
        )
    );
  });
  const unassignedEvents = computed(() => {
    if (!calendar.value) return [];
    return events.value
      .filter((event) => event.appointment && !event.technician)
      .map((event) => {
        return new TimelineEvent({
          id: event.id,
          title: calendar.value?.getEventTitle(event) || "",
          start: calendar.value?.getEventStart(event) || "",
          end: calendar.value?.getEventEnd(event) || "",
          resource: event.technician?.id || -1,
          ghost: Boolean(event.appointment.ghost),
          editable: event.appointment.status === "pending",
          details: event
        });
      });
  });
  watch(
    events,
    (val, oldVal) => {
      if (val !== oldVal) {
        calendar.value?.setEvents(val);
      }
    },
    { deep: true }
  );
  watch(resources, (val, oldVal) => {
    if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
      setResources(val);
      // applyTechnicianOffHourHighlights(val);
      // Apply active resource query param on mobile only
      if (val.length > oldVal.length && breakpoints().sm) {
        const params = getQueryVariables();
        if (params.resource) {
          activeResource.value = resources.value.find(
            (resource: ResourceAPIResponse) =>
              resource.id.toString() === params.resource
          );
          if (activeResource.value) {
            setActiveResource(activeResource.value);
          }
        }
      }
    }
  });

  const getDateFilters = (date) => {
    const startOfDay = moment(date).startOf("day");
    const endOfDay = moment(date).endOf("day");
    return { start_date: startOfDay, end_date: endOfDay };
  };

  const sortResources = (resources: ResourceAPIResponse[]) => {
    resources.slice().sort((a, b) => (a.full_name < b.full_name ? -1 : 1));
    return resources;
  };

  const destroyCalendar = () => {
    calendar.value?.destroy();

    stopEventPolling();
    EventBus.$off("refresh-schedule-board", refreshTimeline);
  };

  const fetchTimelineData = (filters: { start_date: Date; end_date: Date }) => {
    return store.dispatch("fetchTimelineEvents", {
      start_date: filters.start_date.toISOString(),
      end_date: filters.end_date.toISOString()
    });
  };

  const fetchResources = () => {
    store.dispatch("fetchTimelineResources");
  };

  const initCalendar = (
    node: HTMLElement | null,
    date: Date,
    options?: MbscEventcalendarOptions
  ) => {
    if (node) {
      const zoomLevel = localStorage.getItem("scheduler-zoom") || 50;
      calendar.value = new EventCalendar({
        node,
        options: { ...options, selectedDate: date, zoomLevel }
      });
      loadData(getDateFilters(date));

      startEventPolling();
      connectToChannel();
      // Register edit event click
      window.handleSchedulerEventEdit = (appointmentId) => {
        selectedAppointmentId.value = appointmentId;
        if (window.closeUnassignedModal) {
          window.closeUnassignedModal();
        }
      };
      EventBus.$on("refresh-schedule-board", refreshTimeline);
      applyFilterParam();
    }
  };

  const loadData = (filters: { start_date: Date; end_date: Date }) => {
    // Check if date in store is different than current date before fetching data
    if (store.state.timeline.start_date !== filters.start_date.toISOString()) {
      fetchTimelineData(filters);
      fetchResources();
    } else {
      calendar.value?.navigate(store.state.timeline.start_date);
      calendar.value?.setEvents(events.value);
      setResources(resources.value);
    }
  };

  const setActiveResource = (resource: ResourceAPIResponse) => {
    activeResource.value = resource;
    calendar.value?.setResources([resource]);
    updateQueryParam(
      "resource",
      resource.id === -1 ? null : resource.id.toString()
    );
  };

  const setResources = (resources: ResourceAPIResponse[]) => {
    if (breakpoints().sm) {
      const sortedResources = sortResources(resources);
      // Auto select unassigned if no active resource
      if (
        !activeResource.value ||
        !sortedResources.find((res) => res.id === activeResource.value?.id)
      ) {
        activeResource.value = sortedResources[0];
        calendar.value?.setResources([sortedResources[0]]);
      }
    } else {
      calendar.value?.setResources(resources);
    }
  };

  const applyTechnicianOffHourHighlights = (
    resources: ResourceAPIResponse[]
  ) => {
    console.log("calculate hours");
    const allColors = resources.flatMap((tech) =>
      [0, 1, 2, 3, 4, 5, 6].flatMap((day) =>
        getUnavailableColorsFromBusinessHours(
          tech.business_hours,
          day,
          tech.id,
          window.currentTenant.timezone
        )
      )
    );

    // Pass into Mobiscroll config
    calendar.value?.setOptions({ colors: allColors });
  };

  const setCalendarDate = async (date: Date) => {
    // Ignore same day
    if (moment(getRefDate()).utc().isSame(moment(date).utc(), "day")) return;
    calendar.value?.navigate(date);
    EventBus.$emit("update-schedule-header-date", date);
    await fetchTimelineData(getDateFilters(date));
  };

  const getRefDate = () => {
    const viewDate =
      calendar.value?.getViewDate() || store.state.timeline.start_date;
    if (!viewDate) return new Date();
    return viewDate;
  };
  const setCalendarHours = (totalHours: number) => {
    let options = {};
    switch (totalHours) {
      case 6:
        options = { startTime: "09:00", endTime: "15:00" };
        break;
      case 8:
        options = { startTime: "08:00", endTime: "16:00" };
        break;
      case 10:
        options = { startTime: "07:00", endTime: "17:00" };
        break;
      case 14:
        options = { startTime: "05:00", endTime: "19:00" };
        break;
      case 16:
        options = { startTime: "04:00", endTime: "20:00" };
        break;
      case 18:
        options = { startTime: "03:00", endTime: "21:00" };
        break;
      default:
        options = { startTime: "06:00", endTime: "18:00" }; //12hours
    }
    calendar.value?.setOptions({
      refDate: getRefDate(),
      view: {
        timeline: {
          ...calendar.value.desktopViewSettings.timeline,
          ...options
        }
      }
    });
  };
  const refreshTimeline = () => {
    fetchTimelineData(getDateFilters(store.state.timeline.start_date));
  };

  /**
   * Every drag affordance, off while the board is presented as a sheet so a
   * write can only happen through a form.
   *
   * Owned here because both timelines need it: the calendar's own
   * `mobileOptions` do the same job, but only below the `sm` breakpoint
   * (640px), while the sheet gate is `md` (853px). The band between them is
   * ungated in whichever component renders there, and which one that is is a
   * question SchedulePageV2 may yet answer differently.
   *
   * Merged into the options at construction by each timeline, so the initial
   * render already has the right answer.
   */
  const dragOptionsFor = (isSheet: boolean) => {
    const draggable = !isSheet;
    return {
      dragToCreate: draggable,
      dragToMove: draggable,
      dragToResize: draggable,
      dragInTime: draggable,
      dragBetweenResources: draggable,
      externalDrop: draggable
    };
  };

  /**
   * Re-applies the gate after construction, for a placement that changes while
   * the board is open — a resize across the breakpoint, or the tenant flags
   * settling after first paint.
   *
   * `refDate` goes with it for the reason the other setOptions callers pass it:
   * setOptions re-renders the grid, and without it the view can jump off the
   * date the user is looking at.
   */
  const applyDragGate = (isSheet: boolean) => {
    if (!calendar.value) return;
    calendar.value.setOptions({
      refDate: getRefDate(),
      ...dragOptionsFor(isSheet)
    });
  };
  const setCalendarZoom = (zoomLevel: 25 | 50 | 75 | 100) => {
    calendar.value?.setOptions({
      refDate: getRefDate(),
      zoomLevel: zoomLevel
    });
    refreshTimeline();
    localStorage.setItem("scheduler-zoom", zoomLevel);
  };
  const reRenderResources = () => {
    setResources(resources.value);
  };
  const startEventPolling = () => {
    stopEventPolling();
    // Update schedule every 2 minutes
    eventPoll.value = setInterval(() => {
      refreshTimeline();
    }, 120_000);
  };
  const stopEventPolling = () => {
    if (eventPoll.value) {
      clearInterval(eventPoll.value);
    }
  };
  const connectToChannel = () => {
    if (!store.state.tenant?.time_cards_enabled) return;
    channel.value = new AdminTimeClockChannel();

    if (channel.value) {
      cable
        .subscribe(channel.value)
        .then(() => {
          channel.value?.on(
            "tenant-clock-in-status-change",
            handleStatusChange
          );
        })
        .catch((err) => {
          console.log("App Unable to join Cable server", err);
        });
    }
  };

  const handleStatusChange = (payload) => {
    const { user_id, action } = payload;
    if (action === "clock_in") {
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).removeClass(
        "bg-red-500"
      );
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).addClass(
        "bg-green-500"
      );
    } else {
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).removeClass(
        "bg-green-500"
      );
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).addClass(
        "bg-red-500"
      );
    }
  };
  const setTimezoneLabel = () => {
    setTimeout(() => {
      const timeIndicator = document.querySelector(
        ".mbsc-schedule-time-indicator-time"
      );
      if (
        timeIndicator &&
        !timeIndicator.contains(document.querySelector(".timezone-label"))
      ) {
        const timezone = document.createElement("span");
        timezone.classList.add("timezone-label");
        timezone.innerHTML = ` (${window.currentTenant.timezone_abbreviation})`;
        timeIndicator.appendChild(timezone);
      }
    });
  };
  const handleEventRightClick = (args: MbscEventClickEvent) => {
    args.domEvent.preventDefault();
    const event = args.event.details;
    if (!event?.appointment) return;
    const content = document.createElement("div");
    content.innerHTML = `
      <div class="bg-white shadow rounded-lg border p-2 *:cursor-pointer *:p-2 *:rounded-lg">
        <div class="hover:bg-blue-50" onclick="handleEdit(${
          event.appointment.id
        })">Edit</div>
        <div class="hover:bg-blue-50 ${
          event.appointment.status !== "pending" ? "text-gray-400" : ""
        }" onclick="${
      event.appointment.status === "pending"
        ? `handleEdit(${event.appointment.id})`
        : ""
    }">Cancel</div>
        <a class="hover:bg-blue-50 block no-underline text-gray-900" href="${withStartParam(
          jobShowPath(event.appointment.job_id, event.appointment.id)
        )}">View Appointment</a>
        <a class="hover:bg-blue-50 block no-underline text-gray-900" href="${userShowPath(
          event.appointment.customer.id
        )}">View Customer</a>
      </div>
      `;
    document.body.appendChild(content);

    // options with default values
    const options: PopoverOptions = {
      placement: "bottom-end",
      triggerType: "click",
      onHide: () => {
        popover.destroy();
      }
    };
    const popover: PopoverInterface = new Popover(
      content,
      args.domEvent.target,
      options
    );

    popover.show();
  };

  // Tracked here rather than in either timeline: this is the single point both
  // the phone and the desktop board route a card tap through, so the event is
  // counted once and carries the same shape for either surface. trackMobileWebEvent
  // stamps the layout, which is what makes "taps on phone widths" answerable.
  const handleEventClick = (args: MbscEventClickEvent) => {
    const event = args.event.details;
    if (!event?.appointment) {
      trackMobileWebEvent("schedule_card_tap", {
        board: "v2",
        target: "calendar_event"
      });
      return (selectedEventId.value = args.event.id);
    }
    trackMobileWebEvent("schedule_card_tap", {
      board: "v2",
      target: "appointment",
      appointment_id: event.appointment.id
    });
    selectedAppointmentId.value = event.appointment.id;
  };

  /**
   * The technician a tapped cell belongs to, as the create form wants it.
   *
   * Mobiscroll resources are built with `id: tech.id`, which the jbuilder sets
   * to the technician PROFILE id, while the form's technician options are keyed
   * on `user_id` — so handing the resource id straight to the form would either
   * select the wrong technician or none.
   *
   * Everything without a technician behind it falls out of the same lookup miss:
   * the -1 "Unassigned" pseudo-row lives only in the local `resources` computed
   * and never in the store, a department group row carries a string id no
   * technician row can match, and an id dropped from the list matches nothing.
   * Ids compare as strings because those group rows put a string in the field
   * technician rows put a number in.
   */
  const technicianUserIdForResource = (
    resourceId: number | string | undefined
  ) => {
    const match = store.state.timeline.resources.find(
      (resource: ResourceAPIResponse) =>
        String(resource.id) === String(resourceId)
    );
    return match?.user_id ?? undefined;
  };

  /**
   * The instant a tapped cell stands for, in the tenant's timezone.
   *
   * Mobiscroll does not hand back a usable instant. With the timezone plugins
   * configured it builds a Date whose BROWSER-LOCAL getters read back as the
   * wall clock it painted — tap the 8 AM row on the 20th and `getHours()` is 8
   * and `getDate()` is 20, whatever zone either side is in. Passing that Date to
   * a tenant-pinned `moment()` reinterprets the instant instead of the reading,
   * so a browser east of the tenant lands on the previous day: the tap is then
   * swallowed by the past-day guard, or books a day early.
   *
   * So every field is taken from the cell's own reading and rebuilt in the
   * tenant's zone. The board's date is deliberately NOT consulted: `getRefDate`
   * falls back to `store.state.timeline.start_date`, which is only committed
   * when a fetch lands, so between navigating and that response it still holds
   * the previous day.
   */
  const slotFromCell = (cellDate: MbscCellClickEvent["date"]) => {
    const painted = cellDate instanceof Date ? cellDate : new Date(cellDate);
    if (Number.isNaN(painted.getTime())) return null;

    return moment([
      painted.getFullYear(),
      painted.getMonth(),
      painted.getDate(),
      painted.getHours(),
      painted.getMinutes()
    ]);
  };

  /**
   * A tapped empty cell, held until the create form has been built from it.
   *
   * Mirrors selectedAppointmentId / selectedEventId: the composable records
   * what was tapped, and the timeline decides whether to present anything.
   */
  const createSlot = ref<{
    date: Date;
    // The tapped time as a tenant-offset ISO string. The availability picker
    // reads it with `moment.parseZone` and preselects the slot on that hour,
    // falling back to the nearest one, so the offset has to survive the trip —
    // a bare Date would be re-read in the browser's zone and select the wrong
    // hour for any tenant the dispatcher is not sitting in.
    scheduledTime: string;
    technicianUserId?: number;
  } | null>(null);

  const handleCellClick = (args: MbscCellClickEvent) => {
    // A create form already built from an earlier tap. Its backdrop covers the
    // grid, so in a browser this is unreachable — but useAppointmentSchedulingFields
    // is a provideHook, and a second form in the same tree would silently inherit
    // the first one's formData, so the cheap guard is worth having.
    if (createSlot.value) return;

    const slot = slotFromCell(args.date);

    // A past day is dropped rather than carried: the availability picker only
    // searches today onward, so passing one would search a day it cannot book.
    // Same rule CreateEventButton applies to the board date.
    if (!slot || slot.isBefore(moment(), "day")) return;

    createSlot.value = {
      date: slot.toDate(),
      scheduledTime: slot.format(),
      technicianUserId: technicianUserIdForResource(args.resource)
    };
    trackMobileWebEvent("create_sheet_open", { source: "empty_slot" });
  };

  const clearCreateSlot = () => {
    createSlot.value = null;
  };

  const applyFilterParam = () => {
    const params = getQueryVariables();
    const filters: {
      departmentIds?: number[];
      serviceCategoryIds?: number[];
      statuses?: string[];
      jobStatusIds?: string[];
    } = {};
    if (params.departmentIds) {
      filters.departmentIds = params.departmentIds
        .split(",")
        .map((id) => parseInt(id, 10));
    }
    if (params.serviceCategoryIds) {
      filters.serviceCategoryIds = params.serviceCategoryIds
        .split(",")
        .map((id) => parseInt(id, 10));
    }
    if (params.statuses) {
      filters.statuses = params.statuses.split(",");
    }
    if (params.jobStatusIds) {
      filters.jobStatusIds = params.jobStatusIds.split(",");
    }
    store.dispatch("setTimelineFilters", filters);
  };

  return {
    activeResource,
    calendar,
    events,
    resources,
    unassignedEvents,
    selectedAppointmentId,
    selectedEventId,
    createSlot,

    applyDragGate,
    clearCreateSlot,
    dragOptionsFor,
    handleCellClick,
    destroyCalendar,
    initCalendar,
    setActiveResource,
    setCalendarDate,
    setCalendarHours,
    setCalendarZoom,
    refreshTimeline,
    fetchResources,
    reRenderResources,
    setTimezoneLabel,
    handleEventRightClick,
    handleEventClick
  };
});
